import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Typography,
  Alert,
  InputAdornment,
  Chip,
  Divider,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { APIHOST } from '../../common';
import { useApp } from '../AppContext';
import { User } from '../../types/user';
import { getToken } from '../auth/AuthContext';

interface ChangeOrderBookerProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
}

interface Order {
  id: number;
  documentId: string;
  confirmation: string;
  booker?: {
    id: number;
    documentId: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
  };
}

const ChangeOrderBooker: React.FC<ChangeOrderBookerProps> = ({
  open,
  onClose,
  orderId,
}) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string>('');
  const { setSnackbarInput } = useApp();

  // Load order data when dialog opens
  useEffect(() => {
    if (open && orderId) {
      fetchOrder();
    } else {
      // Reset state when dialog closes
      setOrder(null);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUser(null);
      setError('');
    }
  }, [open, orderId]);

  // Search users with debounce
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const timeoutId = setTimeout(() => {
        searchUsers();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchOrder = async () => {
    try {
      setLoadingOrder(true);
      setError('');
      
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}?populate=booker`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      setOrder(data.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch order information');
    } finally {
      setLoadingOrder(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams({
        'filters[$or][0][username][$containsi]': searchQuery.trim(),
        'filters[$or][1][email][$containsi]': searchQuery.trim(),
        'filters[$or][2][firstName][$containsi]': searchQuery.trim(),
        'filters[$or][3][lastName][$containsi]': searchQuery.trim(),
        'pagination[pageSize]': '10',
        'pagination[page]': '1'
      });

      const response = await fetch(`${APIHOST}/api/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to search users');
      }

      const data = await response.json();
      setSearchResults(data || []);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const updateOrderBooker = async () => {
    if (!selectedUser || !order) {
      return;
    }

    try {
      setUpdating(true);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderId}/updateBooker`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          booker: selectedUser.documentId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update order booker: ${response.status} - ${response.statusText}`);
      }

      setSnackbarInput({
        message: `Successfully updated booker to ${selectedUser.firstName} ${selectedUser.lastName}`,
        severity: 'success'
      });
      
      onClose();
    } catch (error) {
      console.error('Error updating order booker:', error);
      setError(error instanceof Error ? error.message : 'Failed to update order booker');
      setSnackbarInput({
        message: error instanceof Error ? error.message : 'Failed to update order booker',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const getUserDisplayName = (user: User) => {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.username || user.email;
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        Change Order Booker
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingOrder ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : order ? (
          <Box>
            {/* Order Information */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Order Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Confirmation:</strong> {order.confirmation}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Current Booker:</strong>{' '}
                {order.booker ? 
                  `${order.booker.firstName} ${order.booker.lastName} (${order.booker.email})` : 
                  'No booker assigned'
                }
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Selected User Display */}
            {selectedUser && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  New Booker Selected:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {getUserDisplayName(selectedUser)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                  <Button 
                    size="small" 
                    onClick={() => setSelectedUser(null)}
                    sx={{ ml: 'auto' }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            )}

            {/* User Search */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Search for New Booker:
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users by name, username, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searching && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              {/* Search Results */}
              {searchResults.length > 0 && (
                <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {searchResults.slice(0, 10).map((user) => (
                      <ListItem key={user.id} disablePadding>
                        <ListItemButton onClick={() => handleUserSelect(user)}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.400' }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {getUserDisplayName(user)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                            {user.userType && (
                              <Chip 
                                label={user.userType} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {searchQuery.trim() && !searching && searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  No users found matching "{searchQuery}"
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updating}>
          Cancel
        </Button>
        <Button 
          onClick={updateOrderBooker} 
          variant="contained" 
          disabled={!selectedUser || updating || loadingOrder}
        >
          {updating ? <CircularProgress size={20} /> : 'Update Booker'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeOrderBooker;